import { useCallback, useEffect, useRef, useState } from "react";

// Drag-to-resize + collapsible rails for the workbench shell. Mirrors the
// behavior of apps/web/layout.js so the React app honors the same CSS grid
// (--col-session / --col-context / --col-browser) and class toggles
// (.session-collapsed / .context-collapsed) that the shared stylesheet expects.

const STORAGE = {
  sessionWidth: "agentguard-layout-session-width",
  contextWidth: "agentguard-layout-context-width",
  browserWidth: "agentguard-layout-browser-width",
  sessionCollapsed: "agentguard-layout-session-collapsed",
  contextCollapsed: "agentguard-layout-context-collapsed",
} as const;

const MIN_RAIL = 160;
const MAX_RAIL = 560;
const MIN_BROWSER = 280;
const MAX_BROWSER = 900;

type Target = "session" | "context" | "browser";

function clampRail(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function readStoredFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function persistFlag(key: string, value: boolean): void {
  try {
    localStorage.setItem(key, value ? "1" : "0");
  } catch {
    // hardened webviews may block localStorage; layout still works in-session.
  }
}

function persistWidth(key: string, width: number): void {
  try {
    localStorage.setItem(key, String(Math.round(width)));
  } catch {
    // see persistFlag
  }
}

export interface SplitterProps {
  onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
  onDoubleClick: () => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  "aria-label": string;
  title: string;
}

export interface UseWorkbenchLayoutResult {
  shellRef: React.RefObject<HTMLDivElement | null>;
  sessionCollapsed: boolean;
  contextCollapsed: boolean;
  setSessionCollapsed: (collapsed: boolean) => void;
  setContextCollapsed: (collapsed: boolean) => void;
  splitterProps: (target: Target) => SplitterProps;
}

const LABELS: Record<Target, { open: string; collapsed: string }> = {
  session: { open: "Resize sidebar", collapsed: "Show sidebar" },
  context: { open: "Resize inspector", collapsed: "Show inspector" },
  browser: { open: "Resize browser", collapsed: "Resize browser" },
};

export function useWorkbenchLayout(): UseWorkbenchLayoutResult {
  const shellRef = useRef<HTMLDivElement>(null);
  const [sessionCollapsed, setSessionCollapsedState] = useState<boolean>(() =>
    readStoredFlag(STORAGE.sessionCollapsed)
  );
  const [contextCollapsed, setContextCollapsedState] = useState<boolean>(() =>
    readStoredFlag(STORAGE.contextCollapsed)
  );

  // Restore stored widths once on mount. After that the user's drag updates
  // the CSS var directly via inline style, which always beats restored values.
  useEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const sessionWidth = parseInt(localStorage.getItem(STORAGE.sessionWidth) || "", 10);
    const contextWidth = parseInt(localStorage.getItem(STORAGE.contextWidth) || "", 10);
    const browserWidth = parseInt(localStorage.getItem(STORAGE.browserWidth) || "", 10);
    if (Number.isFinite(sessionWidth))
      shell.style.setProperty("--col-session", `${clampRail(sessionWidth, MIN_RAIL, MAX_RAIL)}px`);
    if (Number.isFinite(contextWidth))
      shell.style.setProperty("--col-context", `${clampRail(contextWidth, MIN_RAIL, MAX_RAIL)}px`);
    if (Number.isFinite(browserWidth))
      shell.style.setProperty(
        "--col-browser",
        `${clampRail(browserWidth, MIN_BROWSER, MAX_BROWSER)}px`
      );
  }, []);

  const setSessionCollapsed = useCallback((collapsed: boolean) => {
    setSessionCollapsedState(collapsed);
    persistFlag(STORAGE.sessionCollapsed, collapsed);
  }, []);

  const setContextCollapsed = useCallback((collapsed: boolean) => {
    setContextCollapsedState(collapsed);
    persistFlag(STORAGE.contextCollapsed, collapsed);
  }, []);

  const splitterProps = useCallback(
    (target: Target): SplitterProps => {
      const cssVar =
        target === "session"
          ? "--col-session"
          : target === "browser"
            ? "--col-browser"
            : "--col-context";
      const storageKey =
        target === "session"
          ? STORAGE.sessionWidth
          : target === "browser"
            ? STORAGE.browserWidth
            : STORAGE.contextWidth;
      const isCollapsed =
        target === "session" ? sessionCollapsed : target === "context" ? contextCollapsed : false;
      const setCollapsed =
        target === "session"
          ? setSessionCollapsed
          : target === "context"
            ? setContextCollapsed
            : () => {};
      const railSelector =
        target === "session"
          ? ".session-rail"
          : target === "browser"
            ? ".browser-dock"
            : ".context-rail";
      const minW = target === "browser" ? MIN_BROWSER : MIN_RAIL;
      const maxW = target === "browser" ? MAX_BROWSER : MAX_RAIL;
      const label = isCollapsed ? LABELS[target].collapsed : LABELS[target].open;

      function readCurrentWidth(): number {
        const shell = shellRef.current;
        if (!shell) return 260;
        const raw = getComputedStyle(shell).getPropertyValue(cssVar).trim();
        const parsed = parseFloat(raw);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        const rail = shell.querySelector(railSelector);
        return rail ? (rail as HTMLElement).getBoundingClientRect().width : 260;
      }

      function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
        const shell = shellRef.current;
        if (!shell || event.button !== 0) return;
        // Collapsed splitter acts as an expand handle. Without this there's no
        // in-place way to recover from a collapse other than the topbar/icon.
        if (isCollapsed) {
          setCollapsed(false);
          event.preventDefault();
          return;
        }
        event.preventDefault();
        const splitter = event.currentTarget;
        const startX = event.clientX;
        const startWidth = readCurrentWidth();
        splitter.classList.add("is-dragging");
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";

        const onMove = (ev: PointerEvent) => {
          const dx = ev.clientX - startX;
          // Session rail grows to the right; context + browser grow to the left.
          const next = target === "session" ? startWidth + dx : startWidth - dx;
          shell.style.setProperty(cssVar, `${clampRail(next, minW, maxW)}px`);
        };

        const onUp = () => {
          splitter.classList.remove("is-dragging");
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          const finalWidth = parseFloat(getComputedStyle(shell).getPropertyValue(cssVar));
          if (Number.isFinite(finalWidth)) persistWidth(storageKey, finalWidth);
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
      }

      function onDoubleClick() {
        const shell = shellRef.current;
        if (!shell) return;
        shell.style.removeProperty(cssVar);
        try {
          localStorage.removeItem(storageKey);
        } catch {
          /* see persistFlag */
        }
      }

      function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
        const shell = shellRef.current;
        if (!shell) return;
        const step = event.shiftKey ? 64 : 16;
        let delta = 0;
        if (event.key === "ArrowLeft") delta = target === "session" ? -step : step;
        else if (event.key === "ArrowRight") delta = target === "session" ? step : -step;
        else return;
        event.preventDefault();
        const next = clampRail(readCurrentWidth() + delta, minW, maxW);
        shell.style.setProperty(cssVar, `${next}px`);
        persistWidth(storageKey, next);
      }

      return {
        onPointerDown,
        onDoubleClick,
        onKeyDown,
        "aria-label": label,
        title: label,
      };
    },
    [sessionCollapsed, contextCollapsed, setSessionCollapsed, setContextCollapsed]
  );

  return {
    shellRef,
    sessionCollapsed,
    contextCollapsed,
    setSessionCollapsed,
    setContextCollapsed,
    splitterProps,
  };
}
